import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { arcPath, arcMidAngle, polarToCartesian } from '../../utils/charts/svg-utils.ts';
import { DEFAULT_CHART_COLORS } from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import type { ChartDataPoint, ChartOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// PieChart – pure SVG pie chart (full circle, no inner radius)
// ---------------------------------------------------------------------------

interface PieChartSignature {
  Args: ChartOptions & {
    /** Chart data points */
    data: ChartDataPoint[];
  };
  Element: HTMLDivElement;
}

interface PieSegment {
  path: string;
  color: string;
  data: ChartDataPoint;
  percent: number;
  labelX: number;
  labelY: number;
}

const MIN_SIZE = 160;

export default class PieChart extends Component<PieChartSignature> {
  @tracked containerWidth = 300;
  @tracked containerHeight = 300;
  @tracked hoveredIndex: number | null = null;

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get total() {
    return this.args.data.reduce((sum, d) => sum + d.value, 0) || 1;
  }

  get size() {
    const usable = Math.min(this.containerWidth, this.containerHeight);
    return Math.max(MIN_SIZE, usable);
  }

  get outerRadius() {
    return (this.size - 4) / 2;
  }

  get cx() {
    return this.size / 2;
  }

  get cy() {
    return this.size / 2;
  }

  get viewBox() {
    return `0 0 ${this.size} ${this.size}`;
  }

  get segments(): PieSegment[] {
    const { data } = this.args;
    if (!data.length) return [];

    let currentAngle = 0;
    return data.map((d, i) => {
      const fraction = d.value / this.total;
      const startAngle = currentAngle;
      const endAngle = currentAngle + fraction * Math.PI * 2 - 0.005;
      currentAngle += fraction * Math.PI * 2;

      const mid = arcMidAngle(startAngle, endAngle);
      const labelPos = polarToCartesian(this.cx, this.cy, this.outerRadius * 0.6, mid);

      return {
        path: arcPath({
          cx: this.cx,
          cy: this.cy,
          outerRadius: this.outerRadius,
          innerRadius: 0,
          startAngle,
          endAngle: Math.max(endAngle, startAngle + 0.01),
        }),
        color: this.colors[i % this.colors.length]!,
        data: d,
        percent: Math.round(fraction * 100),
        labelX: labelPos.x,
        labelY: labelPos.y,
      };
    });
  }

  /** The hovered slice — drives the in-chart label (donut shows this in its hole). */
  get hoveredSegment(): PieSegment | null {
    if (this.hoveredIndex === null) return null;
    return this.segments[this.hoveredIndex] ?? null;
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get legendPosition() {
    return this.args.legendPosition ?? 'bottom';
  }

  get isSideLegend() {
    return this.legendPosition === 'left' || this.legendPosition === 'right';
  }

  // Flex direction places the legend on the requested side; default 'bottom'
  // reproduces the original column layout.
  get rootClass() {
    switch (this.legendPosition) {
      case 'top':
        return 'flex flex-col-reverse items-center gap-4';
      case 'left':
        return 'flex flex-row-reverse items-center justify-center gap-4';
      case 'right':
        return 'flex flex-row items-center justify-center gap-4';
      default:
        return 'flex flex-col items-center gap-4';
    }
  }

  get chartWrapperClass() {
    return this.isSideLegend
      ? 'tio-flex-center relative flex-1 min-h-0'
      : 'tio-flex-center relative w-full';
  }

  get legendClass() {
    return this.isSideLegend
      ? 'flex flex-col gap-2 text-sm shrink-0'
      : 'grid grid-cols-2 gap-x-4 gap-y-2 text-sm';
  }

  get legendItems() {
    return this.args.data.map((d, i) => ({
      label: d.category,
      color: this.colors[i % this.colors.length]!,
      value: d.value,
      percent: Math.round((d.value / this.total) * 100),
    }));
  }

  get svgStyle() {
    return htmlSafe(`width: ${this.size}px; height: ${this.size}px;`);
  }

  installObserver = modifier((element: HTMLElement) => {
    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      this.containerHeight = Math.max(MIN_SIZE, entry.contentRect.height || MIN_SIZE);
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 300;
    this.containerHeight = Math.max(MIN_SIZE, element.clientHeight || MIN_SIZE);

    return () => observer.disconnect();
  });

  handleSegmentClick = (segment: PieSegment) => {
    this.args.onClick?.(segment.data);
  };

  setHovered = (index: number) => {
    this.hoveredIndex = index;
  };

  clearHovered = () => {
    this.hoveredIndex = null;
  };

  <template>
    <div data-test-chart data-test-chart-type="pie" class={{this.rootClass}} ...attributes>
      <div class={{this.chartWrapperClass}} {{this.installObserver}}>
        <svg
          role="img"
          aria-label="Pie chart"
          style={{this.svgStyle}}
          viewBox={{this.viewBox}}
          class="overflow-visible"
        >
          {{#each this.segments as |seg i|}}
            <path
              data-test-chart-segment
              d={{seg.path}}
              fill={{seg.color}}
              class="transition-transform duration-150 origin-center cursor-pointer"
              style={{if
                (isEqual this.hoveredIndex i)
                (htmlSafe "transform: scale(1.03); filter: brightness(1.1);")
                (htmlSafe "")
              }}
              role="button"
              aria-label="{{seg.data.category}}: {{seg.data.value}} ({{seg.percent}}%)"
              {{on "mouseenter" (fn this.setHovered i)}}
              {{on "mouseleave" this.clearHovered}}
              {{on "click" (fn this.handleSegmentClick seg)}}
            />
          {{/each}}

          {{! Hovered slice label at the slice's mid-angle (pie has no hole to
              show it in, unlike the donut) — outlined for contrast on any color. }}
          {{#if this.hoveredSegment}}
            <g pointer-events="none" data-test-pie-hover-label>
              <text
                x={{this.hoveredSegment.labelX}}
                y={{this.hoveredSegment.labelY}}
                text-anchor="middle"
                dominant-baseline="central"
                style={{htmlSafe
                  "font-size: 14px; font-weight: 700; fill: #fff; paint-order: stroke; stroke: rgba(15,23,42,0.65); stroke-width: 3px;"
                }}
              >{{this.hoveredSegment.percent}}%</text>
              <text
                x={{this.hoveredSegment.labelX}}
                y={{add this.hoveredSegment.labelY 16}}
                text-anchor="middle"
                dominant-baseline="central"
                style={{htmlSafe
                  "font-size: 11px; font-weight: 500; fill: #fff; paint-order: stroke; stroke: rgba(15,23,42,0.65); stroke-width: 3px;"
                }}
              >{{this.hoveredSegment.data.category}}</text>
            </g>
          {{/if}}
        </svg>
      </div>

      {{#if this.showLegend}}
        <div data-test-chart-legend class={{this.legendClass}}>
          {{#each this.legendItems as |item|}}
            <div class="flex items-center gap-1.5">
              <span
                class="inline-block w-3 h-3 rounded-full shrink-0"
                style={{htmlSafe (concat "background-color: " item.color ";")}}
              ></span>
              <span class="tio-text-default font-medium truncate">{{item.label}}</span>
              <span class="tio-text-muted shrink-0">({{item.percent}}%)</span>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

function add(a: number, b: number) {
  return a + b;
}

function concat(...args: (string | number)[]) {
  return args.join('');
}

export type { PieChartSignature };
